import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const skeletonVariants = cva(
  [
    "aw-skeleton",
    "relative",
    "block",
    "overflow-hidden",
    "bg-surface-container",
  ],
  {
    variants: {
      radius: {
        none: "rounded-(--radius-none)",
        sm: "rounded-(--radius-sm)",
        md: "rounded-(--radius-md)",
        lg: "rounded-(--radius-lg)",
        xl: "rounded-(--radius-xl)",
        full: "rounded-(--radius-full)",
      },
    },
    defaultVariants: {
      radius: "sm",
    },
  },
);

type SkeletonRadius = NonNullable<
  VariantProps<typeof skeletonVariants>["radius"]
>;

type SkeletonBaseProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "color"
>;

export interface SkeletonProps
  extends SkeletonBaseProps,
    VariantProps<typeof skeletonVariants> {
  /** Width of the placeholder. Prefer token-backed classes for repeated layouts. */
  width?: React.CSSProperties["width"];
  /** Height of the placeholder. Prefer token-backed classes for repeated layouts. */
  height?: React.CSSProperties["height"];
}

/**
 * Token-backed placeholder shown while content is loading.
 *
 * @example
 * <Skeleton width={120} height={20} />
 * <Skeleton width={40} height={40} radius="full" />
 */
function Skeleton({
  radius = "sm",
  width,
  height,
  className,
  style,
  "aria-hidden": ariaHidden = true,
  ...htmlProps
}: SkeletonProps) {
  const skeletonStyle = {
    ...style,
    ...(width !== undefined ? { width } : undefined),
    ...(height !== undefined ? { height } : undefined),
  };

  return (
    <div
      {...htmlProps}
      data-slot="skeleton"
      data-radius={radius}
      aria-hidden={ariaHidden}
      className={cn(skeletonVariants({ radius }), className)}
      style={skeletonStyle}
    />
  );
}

export { Skeleton };
export type { SkeletonRadius };
